import { cn } from '@/lib/utils';
import { Link } from '@tanstack/react-router';
import {
  BadgeCheck,
  BarChart3,
  Bot,
  Boxes,
  Building2,
  FileText,
  Inbox,
  LayoutDashboard,
  type LucideIcon,
  Megaphone,
  MessageSquare,
  Plug,
  Shield,
  User,
  Users,
  Webhook,
  Workflow,
} from 'lucide-react';

interface SidebarItem {
  to: string;
  label: string;
  icon: LucideIcon;
  /** Texto curto explicando o que o item é, em português humano. */
  hint?: string;
}

interface SidebarGroup {
  label?: string;
  /** Subtitle explicando o propósito do grupo. */
  description?: string;
  items: SidebarItem[];
}

const groups: SidebarGroup[] = [
  {
    items: [
      {
        to: '/',
        label: 'Dashboard',
        icon: LayoutDashboard,
        hint: 'Health & ops — delivery rate, falhas, conexões ativas',
      },
      {
        to: '/analytics',
        label: 'Analytics',
        icon: BarChart3,
        hint: 'Faturamento, conversão, performance IA vs humano',
      },
    ],
  },
  {
    label: 'Operação',
    description: 'Inbox, log e base de contatos',
    items: [
      {
        to: '/inbox',
        label: 'Inbox',
        icon: Inbox,
        hint: 'Conversas real-time + atendimento humano/IA',
      },
      {
        to: '/messages',
        label: 'Mensagens',
        icon: MessageSquare,
        hint: 'Log de disparos + timeline de eventos',
      },
      {
        to: '/leads',
        label: 'Leads',
        icon: Users,
        hint: 'Base de contatos do funil + revenue tracking',
      },
    ],
  },
  {
    label: 'Envios',
    description: 'Campanhas batch + cadências reativas',
    items: [
      {
        to: '/campaigns',
        label: 'Campanhas',
        icon: Megaphone,
        hint: 'Batch sender (audience + template + agendamento)',
      },
      {
        to: '/cadences',
        label: 'Cadências',
        icon: Workflow,
        hint: 'Régua de camadas reativa por payment-status',
      },
      {
        to: '/templates',
        label: 'Templates',
        icon: FileText,
        hint: 'HSMs aprovados pela Meta — reusados em campaigns + cadences',
      },
    ],
  },
  {
    label: 'Inteligência & Integrações',
    description: 'Bots, BMs e webhooks',
    items: [
      {
        to: '/ai-agents',
        label: 'Agentes IA',
        icon: Bot,
        hint: 'Bots conversacionais com handoff humano via tools',
      },
      {
        to: '/business-managers',
        label: 'Business Managers',
        icon: Building2,
        hint: 'BMs do Meta + WABAs + quality rating + webhooks',
      },
      {
        to: '/connections',
        label: 'Conexões',
        icon: Plug,
        hint: 'Numbers WhatsApp Cloud (Phone ID + access token + BM)',
      },
      {
        to: '/registrations',
        label: 'Registros',
        icon: BadgeCheck,
        hint: 'Registro de números na WhatsApp Cloud API + status por número',
      },
      {
        to: '/webhooks',
        label: 'Webhooks',
        icon: Webhook,
        hint: 'Inbound (Meta/Bob/Meta Ads) + outbound subscriptions',
      },
    ],
  },
  {
    label: 'Conta',
    description: 'Workspace, perfil e API keys',
    items: [
      {
        to: '/account/profile',
        label: 'Perfil',
        icon: User,
        hint: 'Dados pessoais, idioma, timezone',
      },
      {
        to: '/account/tenant',
        label: 'Workspace',
        icon: Building2,
        hint: 'Membros, billing, dados do workspace',
      },
      {
        to: '/account/integrations',
        label: 'Integrações & Webhooks',
        icon: Boxes,
        hint: 'API keys (Bob/OpenAI/Meta Ads) + Webhook secrets HMAC — encrypted at-rest',
      },
      {
        to: '/account/security',
        label: 'Segurança',
        icon: Shield,
        hint: 'Senha, sessions, 2FA',
      },
    ],
  },
];

export function Sidebar() {
  return (
    <aside className="flex w-60 flex-col border-r bg-card">
      <div className="flex h-14 items-center border-b px-4">
        <span className="font-semibold">Disparos</span>
      </div>
      <nav className="flex-1 space-y-4 overflow-y-auto p-2">
        {groups.map((group, idx) => (
          <div key={group.label ?? `group-${idx}`} className="space-y-0.5">
            {group.label ? (
              <div className="px-3 pb-1 pt-1">
                <div className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground/70">
                  {group.label}
                </div>
                {group.description ? (
                  <div className="text-[10px] text-muted-foreground/50">{group.description}</div>
                ) : null}
              </div>
            ) : null}
            {group.items.map((item) => (
              <Link
                key={item.to}
                to={item.to}
                title={item.hint}
                activeOptions={{ exact: item.to === '/' }}
                activeProps={{
                  className: 'bg-accent text-accent-foreground',
                }}
                className={cn(
                  'flex items-center gap-3 rounded-md px-3 py-2 text-sm transition-colors',
                  'text-muted-foreground hover:bg-accent hover:text-accent-foreground',
                )}
              >
                <item.icon className="h-4 w-4" />
                {item.label}
              </Link>
            ))}
          </div>
        ))}
      </nav>
      <div className="border-t p-4 text-xs text-muted-foreground">v0.1.0 — fase 2</div>
    </aside>
  );
}
